import { useState, useEffect } from "react";
import { logError } from "@/lib/logError";
import { Link } from "react-router-dom";
import { Flame, Heart, Mail, GraduationCap, Microscope, Sparkles, ArrowLeft, Globe } from "lucide-react";

const EMAIL = "contact@fondation-ahlem.ca";

const LOCALE = { fr: "fr-CA", en: "en-CA", de: "de-DE", ar: "ar" };
const fmtMoney = (n, lang) => {
  const v = Math.round(Number(n) || 0);
  const s = v.toLocaleString(LOCALE[lang] || "fr-CA");
  return lang === "en" ? `$${s}` : `${s} $`;
};
const rel = (iso, lang) => {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(LOCALE[lang] || "fr-CA", { numeric: "auto" });
    const days = Math.round(diff / 86400);
    if (Math.abs(days) >= 1) return rtf.format(-days, "day");
    const hrs = Math.round(diff / 3600);
    if (Math.abs(hrs) >= 1) return rtf.format(-hrs, "hour");
    return rtf.format(-Math.max(1, Math.round(diff / 60)), "minute");
  } catch { return ""; }
};

const C = {
  fr: {
    toggle: "EN",
    kicker: "Projet mémoriel",
    name: "Fondation Dr Ahlem AOUACHRI-MAKHLOUF",
    tribute:
      "À la mémoire du Dr Ahlem AOUACHRI-MAKHLOUF — sa rigueur, sa douceur et son dévouement envers ses patients continuent d'inspirer cette plateforme.",
    missionTitle: "Notre mission",
    mission:
      "La Fondation Dr Ahlem AOUACHRI-MAKHLOUF perpétue la mémoire d'une pathologiste dévouée en soutenant l'excellence, la formation et l'innovation en anatomopathologie, au service des patients.",
    valuesTitle: "Nos valeurs",
    values: [
      [Microscope, "Excellence clinique", "Promouvoir la qualité et la précision du diagnostic en pathologie."],
      [Heart, "Compassion", "Replacer l'humain et le patient au cœur de chaque décision."],
      [GraduationCap, "Formation de la relève", "Soutenir l'enseignement et le mentorat des futurs pathologistes."],
      [Sparkles, "Innovation éthique", "Mettre l'intelligence artificielle au service — jamais à la place — du pathologiste."],
    ],
    contactTitle: "Nous joindre",
    contactText: "Pour en savoir plus, proposer un partenariat ou soutenir la fondation.",
    contactBtn: "Écrire à la fondation",
    donateTitle: "Soutenir la fondation",
    donateText: "Votre appui finance la formation, la recherche et l'innovation en pathologie.",
    donateBtn: "Faire un don",
    donateNote: "Paiement sécurisé par Stripe. Un reçu vous sera envoyé par courriel.",
    custom: "Autre",
    amountPh: "Montant (CAD)",
    donateCta: "Faire un don",
    processing: "Redirection vers le paiement…",
    donateErr: "Impossible de démarrer le paiement. Veuillez réessayer.",
    back: "Retour à l'application",
    raisedLabel: "Total collecté",
    donorsLabel: "dons reçus",
    wallTitle: "Mur de remerciements",
    anon: "Un bienfaiteur anonyme",
    wallEmpty: "Soyez le premier à soutenir la fondation.",
    goalLabel: "Objectif de collecte",
    remaining: "restant",
    subjContact: "Prise de contact — Fondation Dr Ahlem AOUACHRI-MAKHLOUF",
    subjDonate: "Je souhaite faire un don — Fondation Dr Ahlem AOUACHRI-MAKHLOUF",
  },
  en: {
    toggle: "FR",
    kicker: "Memorial project",
    name: "Dr Ahlem AOUACHRI-MAKHLOUF Foundation",
    tribute:
      "In memory of Dr Ahlem AOUACHRI-MAKHLOUF — her rigour, gentleness and devotion to her patients continue to inspire this platform.",
    missionTitle: "Our mission",
    mission:
      "The Dr Ahlem AOUACHRI-MAKHLOUF Foundation honours the memory of a devoted pathologist by supporting excellence, education and innovation in anatomic pathology, in the service of patients.",
    valuesTitle: "Our values",
    values: [
      [Microscope, "Clinical excellence", "Advancing diagnostic quality and precision in pathology."],
      [Heart, "Compassion", "Keeping people and patients at the heart of every decision."],
      [GraduationCap, "Training the next generation", "Supporting the teaching and mentoring of future pathologists."],
      [Sparkles, "Ethical innovation", "Putting artificial intelligence at the service of — never in place of — the pathologist."],
    ],
    contactTitle: "Get in touch",
    contactText: "To learn more, propose a partnership or support the foundation.",
    contactBtn: "Email the foundation",
    donateTitle: "Support the foundation",
    donateText: "Your support funds education, research and innovation in pathology.",
    donateBtn: "Make a donation",
    donateNote: "Secure payment by Stripe. A receipt will be emailed to you.",
    custom: "Other",
    amountPh: "Amount (CAD)",
    donateCta: "Donate",
    processing: "Redirecting to payment…",
    donateErr: "Could not start payment. Please try again.",
    back: "Back to the application",
    raisedLabel: "Total raised",
    donorsLabel: "donations received",
    wallTitle: "Wall of thanks",
    anon: "An anonymous benefactor",
    wallEmpty: "Be the first to support the foundation.",
    goalLabel: "Fundraising goal",
    remaining: "remaining",
    subjContact: "Getting in touch — Dr Ahlem AOUACHRI-MAKHLOUF Foundation",
    subjDonate: "I would like to donate — Dr Ahlem AOUACHRI-MAKHLOUF Foundation",
  },
  de: {
    toggle: "AR",
    kicker: "Gedenkprojekt",
    name: "Stiftung Dr. Ahlem AOUACHRI-MAKHLOUF",
    tribute:
      "Zum Gedenken an Dr. Ahlem AOUACHRI-MAKHLOUF — ihre Sorgfalt, ihre Sanftmut und ihre Hingabe an ihre Patienten inspirieren diese Plattform weiterhin.",
    missionTitle: "Unsere Mission",
    mission:
      "Die Stiftung Dr. Ahlem AOUACHRI-MAKHLOUF ehrt das Andenken einer engagierten Pathologin, indem sie Exzellenz, Ausbildung und Innovation in der Anatomopathologie im Dienste der Patienten fördert.",
    valuesTitle: "Unsere Werte",
    values: [
      [Microscope, "Klinische Exzellenz", "Die diagnostische Qualität und Präzision in der Pathologie fördern."],
      [Heart, "Mitgefühl", "Den Menschen und die Patienten in den Mittelpunkt jeder Entscheidung stellen."],
      [GraduationCap, "Ausbildung des Nachwuchses", "Die Lehre und das Mentoring künftiger Pathologen unterstützen."],
      [Sparkles, "Ethische Innovation", "Künstliche Intelligenz in den Dienst — niemals an die Stelle — des Pathologen stellen."],
    ],
    contactTitle: "Kontakt",
    contactText: "Um mehr zu erfahren, eine Partnerschaft vorzuschlagen oder die Stiftung zu unterstützen.",
    contactBtn: "Die Stiftung anschreiben",
    donateTitle: "Die Stiftung unterstützen",
    donateText: "Ihre Unterstützung finanziert Ausbildung, Forschung und Innovation in der Pathologie.",
    donateBtn: "Spenden",
    donateNote: "Sichere Zahlung über Stripe. Eine Quittung wird Ihnen per E-Mail zugesandt.",
    custom: "Andere",
    amountPh: "Betrag (CAD)",
    donateCta: "Spenden",
    processing: "Weiterleitung zur Zahlung…",
    donateErr: "Zahlung konnte nicht gestartet werden. Bitte erneut versuchen.",
    back: "Zurück zur Anwendung",
    raisedLabel: "Gesamtbetrag",
    donorsLabel: "erhaltene Spenden",
    wallTitle: "Dankeswand",
    anon: "Ein anonymer Wohltäter",
    wallEmpty: "Seien Sie die erste Person, die die Stiftung unterstützt.",
    goalLabel: "Spendenziel",
    remaining: "verbleibend",
    subjContact: "Kontaktaufnahme — Stiftung Dr. Ahlem AOUACHRI-MAKHLOUF",
    subjDonate: "Ich möchte spenden — Stiftung Dr. Ahlem AOUACHRI-MAKHLOUF",
  },
  ar: {
    toggle: "FR",
    kicker: "مشروع تذكاري",
    name: "مؤسسة الدكتورة أحلام عواشري-مخلوف",
    tribute:
      "إحياءً لذكرى الدكتورة أحلام عواشري-مخلوف — دقتها ولطفها وتفانيها تجاه مرضاها تلهم هذه المنصة دائمًا.",
    missionTitle: "مهمتنا",
    mission:
      "تُخلّد مؤسسة الدكتورة أحلام عواشري-مخلوف ذكرى طبيبة تشريح مرضي متفانية من خلال دعم التميّز والتكوين والابتكار في علم الأمراض التشريحي، في خدمة المرضى.",
    valuesTitle: "قيمنا",
    values: [
      [Microscope, "التميّز السريري", "تعزيز جودة التشخيص ودقته في علم الأمراض."],
      [Heart, "الرحمة", "وضع الإنسان والمريض في صميم كل قرار."],
      [GraduationCap, "تكوين الأجيال القادمة", "دعم تعليم وإرشاد أطباء علم الأمراض المستقبليين."],
      [Sparkles, "الابتكار الأخلاقي", "وضع الذكاء الاصطناعي في خدمة طبيب علم الأمراض — لا بديلاً عنه."],
    ],
    contactTitle: "تواصل معنا",
    contactText: "لمعرفة المزيد، اقتراح شراكة أو دعم المؤسسة.",
    contactBtn: "مراسلة المؤسسة",
    donateTitle: "ادعم المؤسسة",
    donateText: "دعمك يموّل التكوين والبحث والابتكار في علم الأمراض.",
    donateBtn: "تبرّع",
    donateNote: "دفع آمن عبر Stripe. سيُرسل إليك إيصال عبر البريد الإلكتروني.",
    custom: "آخر",
    amountPh: "المبلغ (CAD)",
    donateCta: "تبرّع",
    processing: "جارٍ التحويل إلى الدفع…",
    donateErr: "تعذّر بدء الدفع. يرجى المحاولة مرة أخرى.",
    back: "العودة إلى التطبيق",
    raisedLabel: "إجمالي ما جُمع",
    donorsLabel: "تبرّعات مستلمة",
    wallTitle: "جدار الشكر",
    anon: "محسن مجهول",
    wallEmpty: "كن أول من يدعم المؤسسة.",
    goalLabel: "هدف الجمع",
    remaining: "متبقٍ",
    subjContact: "التواصل — مؤسسة الدكتورة أحلام عواشري-مخلوف",
    subjDonate: "أرغب في التبرّع — مؤسسة الدكتورة أحلام عواشري-مخلوف",
  },
};

export default function Foundation() {
  const LANGS = ["fr", "en", "de", "ar"];
  const [lang, setLang] = useState(() => { const l = localStorage.getItem("lang"); return ["fr", "en", "de", "ar"].includes(l) ? l : "fr"; });
  const [shown, setShown] = useState(false);
  const [sel, setSel] = useState("t50");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState({ total: 0, count: 0, goal: 25000, wall: [] });
  const t = C[lang];
  const API = process.env.REACT_APP_BACKEND_URL;
  const TIERS = [25, 50, 100, 250];

  const donate = async () => {
    setBusy(true); setErr("");
    try {
      const body = sel === "custom"
        ? { tier: "custom", custom_amount: parseFloat(custom || "0"), origin_url: window.location.origin }
        : { tier: sel, origin_url: window.location.origin };
      const r = await fetch(`${API}/api/donations/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("fail");
      const data = await r.json();
      window.location.href = data.checkout_url;
    } catch {
      setErr(t.donateErr); setBusy(false);
    }
  };

  useEffect(() => {
    document.title = t.name;
    const id = setTimeout(() => setShown(true), 50);
    return () => clearTimeout(id);
  }, [t.name]);

  useEffect(() => {
    fetch(`${API}/api/donations/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setStats({ total: d.total || 0, count: d.count || 0, goal: d.goal || 25000, wall: d.wall || [] }); })
      .catch(() => {});
  }, [API]);

  // Journalisation anonyme de la visite (une fois par session navigateur)
  useEffect(() => {
    try {
      if (sessionStorage.getItem("fdn_visit_logged")) return;
      let vid = localStorage.getItem("fdn_visitor");
      if (!vid) {
        vid = (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
        localStorage.setItem("fdn_visitor", vid);
      }
      fetch(`${API}/api/fondation/visite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor: vid, lang }),
      }).catch(() => {});
      sessionStorage.setItem("fdn_visit_logged", "1");
    } catch (e) { logError("Suivi de visite Fondation")(e); }
  }, [API, lang]);

  const mailto = (subject) => `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}`;

  return (
    <div className="min-h-screen text-slate-100" dir={lang === "ar" ? "rtl" : "ltr"} style={{ background: "#0B1F33" }} data-testid="foundation-page">
      {/* Top bar */}
      <header className="max-w-5xl mx-auto px-6 pt-6 flex items-center justify-between">
        <Link to="/login" data-testid="foundation-back"
              className="inline-flex items-center gap-2 text-sm text-sky-200/80 hover:text-white no-underline transition-colors">
          <ArrowLeft className="h-4 w-4" /> {t.back}
        </Link>
        <button onClick={() => { const nl = LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length]; localStorage.setItem("lang", nl); setLang(nl); }} data-testid="foundation-lang-toggle"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 border border-amber-300/40 rounded-full px-3 py-1.5 hover:bg-amber-300/10 transition-colors">
          <Globe className="h-3.5 w-3.5" /> {LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length].toUpperCase()}
        </button>
      </header>

      {/* Hero */}
      <section className={`max-w-3xl mx-auto px-6 pt-14 pb-10 text-center transition-all duration-700 ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="flex justify-center mb-6">
          <div className="h-24 w-24 rounded-full bg-white p-2.5 ring-4 ring-amber-300/50 shadow-2xl flex items-center justify-center" data-testid="foundation-logo">
            <img src="/foundation-logo.png" alt="Logo Fondation Dr Ahlem AOUACHRI-MAKHLOUF" className="h-full w-full object-contain rounded-full" />
          </div>
        </div>
        <div className="inline-flex items-center gap-2 text-amber-300 text-xs font-semibold tracking-widest uppercase">
          <Flame className="h-4 w-4" /> {t.kicker}
        </div>
        <div className="mt-6 flex justify-center">
          <img src="/ahlem-portrait.jpg" alt="Dr Ahlem AOUACHRI-MAKHLOUF" data-testid="foundation-portrait"
               className="h-40 w-40 rounded-full object-cover ring-4 ring-amber-300/60 shadow-2xl" />
        </div>
        <h1 className="font-head text-4xl sm:text-5xl font-bold mt-7 leading-tight"
            style={{ background: "linear-gradient(90deg,#fde68a,#fff,#fcd34d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {t.name}
        </h1>
        <p className="mt-6 text-base sm:text-lg text-sky-100/90 italic leading-relaxed max-w-2xl mx-auto">
          « {t.tribute} »
        </p>
      </section>

      {/* Mission */}
      <section className="max-w-3xl mx-auto px-6 py-8">
        <div className="rounded-2xl border border-amber-300/20 bg-white/[0.04] p-8 text-center">
          <h2 className="font-head text-2xl font-bold text-amber-200">{t.missionTitle}</h2>
          <p className="mt-4 text-sky-100/90 leading-relaxed">{t.mission}</p>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-5xl mx-auto px-6 py-6">
        <h2 className="font-head text-2xl font-bold text-center text-white mb-8">{t.valuesTitle}</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {t.values.map(([Icon, title, desc], i) => (
            <div key={title} data-testid={`foundation-value-${i}`}
                 className="rounded-xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] transition-colors">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center text-amber-300 bg-amber-300/10 mb-4">
                <Icon className="h-5 w-5" />
              </div>
              <div className="font-semibold text-white">{title}</div>
              <p className="mt-2 text-sm text-sky-100/75 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Impact : compteur + mur de remerciements */}
      <section className="max-w-5xl mx-auto px-6 py-8" data-testid="foundation-impact">
        <div className="rounded-2xl border border-amber-300/20 bg-white/[0.04] p-8">
          <div className="mb-6" data-testid="impact-goal">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs uppercase tracking-widest text-amber-300/80 font-semibold">{t.goalLabel}</span>
              <span className="text-sm font-bold text-amber-200">{Math.min(100, Math.round((stats.total / (stats.goal || 25000)) * 100))}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden" data-testid="impact-progress">
              <div className="h-full rounded-full transition-all duration-700"
                   style={{ width: `${Math.min(100, (stats.total / (stats.goal || 25000)) * 100)}%`, background: "linear-gradient(90deg,#F5C542,#f59e0b)" }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-xs text-sky-100/70">
              <span>{fmtMoney(stats.total, lang)} / {fmtMoney(stats.goal || 25000, lang)}</span>
              <span>{fmtMoney(Math.max(0, (stats.goal || 25000) - stats.total), lang)} {t.remaining}</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-8 items-center">
            <div className="text-center sm:text-start">
              <div className="text-xs uppercase tracking-widest text-amber-300/80 font-semibold">{t.raisedLabel}</div>
              <div className="font-head text-5xl font-bold text-amber-200 mt-2" data-testid="impact-total">{fmtMoney(stats.total, lang)}</div>
              <div className="text-sky-100/70 mt-1 text-sm" data-testid="impact-count">{stats.count.toLocaleString(LOCALE[lang] || "fr-CA")} {t.donorsLabel}</div>
            </div>
            <div>
              <div className="font-head text-lg font-bold text-white mb-3 flex items-center gap-2"><Heart className="h-4 w-4 text-amber-300" /> {t.wallTitle}</div>
              {stats.wall.length ? (
                <div className="flex flex-wrap gap-2" data-testid="impact-wall">
                  {stats.wall.map((w, i) => (
                    <div key={w.id || `${w.nom}-${w.created_at}-${i}`} data-testid={`wall-item-${i}`}
                         className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 leading-tight">
                      <span className="text-amber-200 font-bold text-sm">{fmtMoney(w.amount, lang)}</span>
                      <span className="text-sky-100/55 text-xs"> · {t.anon} · {rel(w.created_at, lang)}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sky-100/60 text-sm" data-testid="impact-wall-empty">{t.wallEmpty}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Contact + Donate */}
      <section className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 flex flex-col">
          <h3 className="font-head text-xl font-bold text-white flex items-center gap-2"><Mail className="h-5 w-5 text-sky-300" /> {t.contactTitle}</h3>
          <p className="mt-3 text-sm text-sky-100/80 leading-relaxed flex-1">{t.contactText}</p>
          <a href={mailto(t.subjContact)} data-testid="foundation-contact-btn"
             className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold px-5 py-3 no-underline transition-colors">
            <Mail className="h-4 w-4" /> {t.contactBtn}
          </a>
        </div>
        <div className="rounded-2xl border border-amber-300/30 bg-amber-300/[0.06] p-8 flex flex-col" data-testid="foundation-donate-card">
          <h3 className="font-head text-xl font-bold text-amber-100 flex items-center gap-2"><Heart className="h-5 w-5 text-amber-300" /> {t.donateTitle}</h3>
          <p className="mt-3 text-sm text-amber-50/85 leading-relaxed">{t.donateText}</p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TIERS.map((n) => {
              const key = `t${n}`;
              const label = lang === "en" ? `$${n}` : `${n} $`;
              return (
                <button key={key} onClick={() => setSel(key)} data-testid={`donate-tier-${n}`}
                        className={`rounded-lg py-2.5 text-sm font-bold border transition-colors ${sel === key ? "bg-amber-400 text-slate-900 border-amber-400" : "border-amber-300/40 text-amber-100 hover:bg-amber-300/10"}`}>
                  {label}
                </button>
              );
            })}
          </div>
          <button onClick={() => setSel("custom")} data-testid="donate-tier-custom"
                  className={`mt-2 rounded-lg py-2.5 text-sm font-semibold border transition-colors ${sel === "custom" ? "bg-amber-400 text-slate-900 border-amber-400" : "border-amber-300/40 text-amber-100 hover:bg-amber-300/10"}`}>
            {t.custom}
          </button>
          {sel === "custom" && (
            <input type="number" min="5" step="1" value={custom} onChange={(e) => setCustom(e.target.value)}
                   data-testid="donate-custom-input" placeholder={t.amountPh}
                   className="mt-2 rounded-lg px-3 py-2.5 bg-white/5 border border-amber-300/40 text-white placeholder:text-amber-100/40 focus:outline-none focus:border-amber-300" />
          )}
          <button onClick={donate} disabled={busy || (sel === "custom" && !(parseFloat(custom) >= 5))} data-testid="foundation-donate-btn"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-slate-900 font-bold px-5 py-3 transition-colors">
            <Heart className="h-4 w-4" /> {busy ? t.processing : t.donateCta}
          </button>
          {err && <p className="mt-2 text-xs text-red-300 text-center" data-testid="donate-error">{err}</p>}
          <p className="mt-3 text-[11px] text-amber-100/60 text-center">{t.donateNote}</p>
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-6 py-10 text-center border-t border-white/10 mt-6">
        <div className="inline-flex items-center gap-2 text-sky-200/70 text-sm">
          <Microscope className="h-4 w-4" /> Path-Intelligent · LIS Pathologie · Québec
        </div>
      </footer>
    </div>
  );
}
